// The Movie Tracker's data shapes and the filtering the History tab does over
// them. The server computes every display field (see
// `server/services/tracker_view.py`); what lives here is only what has to
// happen between a keystroke and the next paint.
//
// Filtering is CLIENT-side, which inverts the Streamlit page. There, every
// character typed into the search box re-ran the whole script and re-filtered a
// DataFrame server-side, so a page with a few hundred films felt slow to type
// into. The whole history arrives once and every control below is array work.

export type LoggedMovie = {
  id: string;
  movie_title: string;
  /** YYYY-MM-DD, or "" when the sheet's date cell would not parse. */
  date: string;
  /** The 1-based row this film occupies in the sheet, or null when the server
   *  could not tell. The tab is append-only and the date carries no time of
   *  day, so this is the only record of the order two films on one date were
   *  seen in. */
  sheet_row: number | null;
  date_label: string;
  /** What the sheet holds. Send this back on a write; show `theater_short`. */
  theater: string;
  theater_short: string;
  auditorium: string;
  seats: string;
  cost: number;
  /** Null means unrated, which is not the same as a rating of zero. */
  rating: number | null;
  notes: string;
  poster_url: string;
  tmdb_id: string;
  letterboxd_id: string;
  /** Present only when this app logged the diary entry itself. */
  letterboxd_url: string | null;
  alist: boolean;
  /** Member keys from the server's config, never sheet column names. */
  alist_members: string[];
};

export type TheaterProgress = {
  theater_short: string;
  seen_count: number;
  visited: string[];
  /** Null when the theater's name carries no auditorium count to divide by. */
  total: number | null;
  missing: string[];
  complete: boolean;
};

export type TrackerMember = { key: string; name: string };

export type HistoryBody = {
  available: boolean;
  /** Why not, when `available` is false: "unreadable" (Sheets is down, it
   *  self-heals) or "malformed" (the read worked and the parse did not, which
   *  is a cell to fix or a bug here). Empty when available. */
  reason?: string;
  movies: LoggedMovie[];
  theaters: string[];
  members: TrackerMember[];
  progress: Record<string, TheaterProgress[]>;
};

export type SortOption = "date-desc" | "date-asc" | "rating-desc" | "title-asc";

export const SORT_LABELS: Record<SortOption, string> = {
  "date-desc": "Date (newest)",
  "date-asc": "Date (oldest)",
  "rating-desc": "Rating (highest)",
  "title-asc": "Title (A-Z)",
};

export type HistoryFilters = {
  /** A member key. The History tab is always scoped to one. */
  member: string;
  search: string;
  /** A short theater name, or "" for all. */
  theater: string;
  minRating: number;
  sort: SortOption;
  /** YYYY-MM-DD bounds, inclusive. "" means unbounded. */
  from: string;
  to: string;
};

export const DEFAULT_FILTERS: Omit<HistoryFilters, "member"> = {
  search: "",
  theater: "",
  minRating: 0,
  sort: "date-desc",
  from: "",
  to: "",
};

/** The films one member's A-List paid for.
 *
 *  BOTH conditions, matching every surface of the Streamlit page and the
 *  server's own `_member_frame`: `alist` says a membership was spent at all,
 *  and the member list says whose. A ticket bought outright carries neither and
 *  belongs in no member's tab.
 */
function forMember(movies: LoggedMovie[], member: string): LoggedMovie[] {
  return movies.filter((m) => m.alist && m.alist_members.includes(member));
}

/** Two films seen on one date, later-logged first.
 *
 *  The sheet is append-only and its `Date` cell holds no time of day, so the
 *  row number is the only thing that knows which of the day's films came
 *  second. A row the server could not number sorts as a tie, leaving the title
 *  to decide, which is what every same-date pair used to do.
 */
function byRow(a: LoggedMovie, b: LoggedMovie): number {
  if (a.sheet_row === null || b.sheet_row === null) return 0;
  return b.sheet_row - a.sheet_row;
}

/** Compare two films for one sort option. Ties fall back to title. */
function compare(a: LoggedMovie, b: LoggedMovie, sort: SortOption): number {
  const byTitle = a.movie_title.toLowerCase().localeCompare(b.movie_title.toLowerCase());
  switch (sort) {
    case "date-asc":
      return a.date.localeCompare(b.date) || byRow(b, a) || byTitle;
    case "rating-desc":
      // Unrated sorts last. Any sentinel below 1 does that identically -- a
      // stored rating is 1-5 or null, never 0 -- so this is about being below
      // the range, not about the particular number.
      return (b.rating ?? -1) - (a.rating ?? -1) || byTitle;
    case "title-asc":
      return byTitle;
    case "date-desc":
    default:
      return b.date.localeCompare(a.date) || byRow(a, b) || byTitle;
  }
}

/** Every filter and the sort, applied in one pass.
 *
 *  A film whose date did not parse survives every filter EXCEPT a date range,
 *  which it fails whichever bound is set. That mirrors pandas, where a
 *  comparison against `NaT` is False on both sides, and it is the behaviour you
 *  want: the row is visible by default so it can be found and fixed, and it
 *  does not silently join a range it may not belong to.
 */
export function filterMovies(
  movies: LoggedMovie[],
  filters: HistoryFilters,
): LoggedMovie[] {
  const needle = filters.search.trim().toLowerCase();
  const bounded = Boolean(filters.from || filters.to);

  return forMember(movies, filters.member)
    .filter((m) => {
      if (needle && !m.movie_title.toLowerCase().includes(needle)) return false;
      if (filters.theater && m.theater_short !== filters.theater) return false;
      if (filters.minRating > 0 && (m.rating ?? 0) < filters.minRating) return false;
      if (bounded) {
        if (!m.date) return false;
        if (filters.from && m.date < filters.from) return false;
        if (filters.to && m.date > filters.to) return false;
      }
      return true;
    })
    .sort((a, b) => compare(a, b, filters.sort));
}

/** Theaters this member has been to, most-visited first.
 *
 *  Built from the member's own rows rather than from the server's `theaters`
 *  list, which is the union of the config and the whole history: offering a
 *  filter for a theater that would match none of the rows on screen reads as a
 *  bug in the filter.
 */
export function theaterOptions(
  movies: LoggedMovie[],
  member: string,
): { name: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const movie of forMember(movies, member)) {
    if (!movie.theater_short) continue;
    counts.set(movie.theater_short, (counts.get(movie.theater_short) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

/** The earliest and latest dated film, for the date inputs' bounds.
 *
 *  Undated rows are skipped rather than sorting to one end, where they would
 *  pin a bound to "" and make the range control useless.
 */
export function dateBounds(movies: LoggedMovie[]): { min: string; max: string } {
  const dates = movies.map((m) => m.date).filter(Boolean).sort();
  return { min: dates[0] ?? "", max: dates[dates.length - 1] ?? "" };
}

/** The stars for a rating, as filled/half/empty counts.
 *
 *  Returned as counts rather than a rendered string so the page can style them;
 *  the half-star threshold is the Streamlit page's, at .5 and above.
 */
export function stars(rating: number | null): { full: number; half: boolean; empty: number } {
  if (!rating || rating <= 0) return { full: 0, half: false, empty: 5 };
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  return { full, half, empty: Math.max(0, 5 - full - (half ? 1 : 0)) };
}

/** The shape every tracker write returns, always under HTTP 200. */
export type WriteResult = {
  ok: boolean;
  movie_id: string;
  letterboxd_id: string;
  letterboxd_orphaned: boolean;
  blocked_before_write: boolean;
  error: string;
};

/** How an orphaned Letterboxd entry is described, for every surface.
 *
 * The id is the only fact that varies. A sheet-write failure knows exactly
 * which entry it stranded; an unconfirmed write does not, because the id never
 * came back. The ADVICE is identical either way and is the part that matters:
 * the entry may be on Letterboxd with nothing here pointing at it, so check
 * before retrying.
 *
 * Lives here rather than beside its callers because exporting a non-component
 * from a route file breaks Fast Refresh for that file, which eslint's
 * `react-refresh/only-export-components` says out loud.
 */
export function orphanNotice(result: WriteResult, title?: string): string {
  const entry = result.letterboxd_id
    ? `the Letterboxd entry (${result.letterboxd_id}) is still there`
    : `a Letterboxd entry may have been created`;
  const lead = title ? `${title}: ` : "";
  return (
    `${lead}${entry} with nothing here pointing at it. Remove it on ` +
    `Letterboxd before retrying, or you may log it twice. (${result.error})`
  );
}
